/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { RestaurantCustomer, CUSTOMER_STATUS_LEVELS } from "@lemmonhq/types";
import { useLocalization, UserAvatar } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import {
  formatCurrency,
  timeAgo,
  isVIP as checkVIP,
  getTotalSpent,
} from "../utils";

const STATUS_BORDER: Record<string, string> = {
  [CUSTOMER_STATUS_LEVELS.VIP]: "border-l-amber-500",
  [CUSTOMER_STATUS_LEVELS.NEW]: "border-l-primary",
  [CUSTOMER_STATUS_LEVELS.REGULAR]: "border-l-base-content/10",
  [CUSTOMER_STATUS_LEVELS.INACTIVE]: "border-l-error/40",
};

export const CustomerCRMCard = defineComponent<{
  customer: RestaurantCustomer;
  isSelected: boolean;
  onSelect: (customer: RestaurantCustomer) => void;
}>({
  name: "CustomerCRMCard",
  props: ["customer", "isSelected", "onSelect"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const vip = computed(() => checkVIP(props.customer));
    const totalSpent = computed(() => getTotalSpent(props.customer));
    const borderClass = computed(
      () =>
        STATUS_BORDER[props.customer.status_level || ""] ||
        "border-l-transparent",
    );
    const lastSeen = computed(() => timeAgo(props.customer.last_order_date, t));
    const hasReservations = computed(
      () => (props.customer.total_reservations ?? 0) > 0,
    );
    const hasNoshows = computed(
      () => (props.customer.total_reservations_noshows ?? 0) > 0,
    );

    return () => (
      <button
        onClick={() => props.onSelect(props.customer)}
        class={`w-full text-left rounded-lg border-l-[3px] transition-[background-color,box-shadow,ring-color,border-color] duration-100 group focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none ${borderClass.value} ${
          props.isSelected
            ? "bg-primary/8 ring-1 ring-primary/20 shadow-sm shadow-primary/5 bg-base-200"
            : "hover:bg-base-100/50 active:bg-base-100/60 motion-safe:active:scale-[0.995]"
        }`}
      >
        <div class="flex items-center gap-3 px-3 py-2.5">
          <UserAvatar name={props.customer.name} email={props.customer.email}>
            {{
              badge: vip.value
                ? () => (
                    <div class="w-full h-full bg-amber-500 flex items-center justify-center">
                      <span class="material-symbols-outlined text-[10px] text-accent font-bold">
                        star
                      </span>
                    </div>
                  )
                : undefined,
            }}
          </UserAvatar>

          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5">
              <span class="text-[13px] font-medium text-accent truncate leading-tight">
                {props.customer.name || t("reservations.customers.no_name")}
              </span>
              {vip.value && (
                <span class="badge badge-xs bg-amber-500/12 text-amber-400 border-amber-500/25 text-[10px] font-bold px-1 py-0">
                  {t("reservations.customers.vip")}
                </span>
              )}
              {hasNoshows.value && (
                <span class="badge badge-xs bg-error/10 text-error/70 border-error/20 text-[10px] px-1 py-0">
                  {t("reservations.customers.no_show_count", [
                    props.customer.total_reservations_noshows ?? 0,
                  ])}
                </span>
              )}
            </div>
            <div class="flex items-center gap-2 mt-0.5">
              <span class="text-[11px] text-base-content/35 truncate">
                {props.customer.email ||
                  props.customer.phone ||
                  t("reservations.customers.no_contact")}
              </span>
              {lastSeen.value && (
                <>
                  <span class="text-base-content/10">·</span>
                  <span class="text-[10px] text-base-content/25 flex-shrink-0">
                    {lastSeen.value}
                  </span>
                </>
              )}
            </div>
          </div>

          <div class="flex-shrink-0 text-right space-y-0.5">
            <div
              class={`text-[13px] font-semibold tabular-nums ${
                totalSpent.value > 0
                  ? "text-success/80"
                  : "text-base-content/25"
              }`}
            >
              {formatCurrency(
                totalSpent.value,
                auth.user.value?.restaurant.currency || "CHF",
              )}
            </div>
            <div class="flex items-center gap-2 justify-end">
              <span class="flex items-center gap-0.5 text-[11px] text-base-content/50">
                <span class="material-symbols-outlined text-[11px]">
                  receipt_long
                </span>
                {props.customer.total_orders ?? 0}
              </span>
              {hasReservations.value && (
                <span class="flex items-center gap-0.5 text-[11px] text-base-content/50">
                  <span class="material-symbols-outlined text-[11px]">
                    event_seat
                  </span>
                  {props.customer.total_reservations}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  },
});
